import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { PublicEdufad } from "@edufadder/shared-types";
import { Chip, colors, fonts, spacing } from "@edufadder/ui";
import { EvidenceChip, topicLabel } from "../components/EvidenceChip.js";
import { HowTheseWereChosen, ReadingCard } from "../components/FurtherReading.js";
import { useAuth } from "../lib/auth-context.js";
import * as api from "../lib/api.js";
import { ApiError } from "../lib/api.js";
import * as s from "../styles.js";

export function EdufadReportPage(): JSX.Element {
  const { claimId } = useParams<{ claimId: string }>();
  const { me } = useAuth();
  const [item, setItem] = useState<PublicEdufad | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  useEffect(() => {
    if (!claimId) return;
    api
      .getPublicEdufad(claimId)
      .then((e) => {
        setItem(e);
        document.title = `${e.label} — EduFadder`;
      })
      .catch((err) => setError(err instanceof ApiError && err.status === 404 ? "This edufad isn't published (or doesn't exist)." : "Couldn't load this report."));
    return () => {
      document.title = "EduFadder — check the evidence behind education claims";
    };
  }, [claimId]);

  async function feedback(kind: string): Promise<void> {
    if (!claimId) return;
    try {
      await api.createFeedback({ kind, claim_id: claimId, note: "" });
      setSent("Thank you — curators review every response.");
    } catch {
      setSent("Couldn't send that just now.");
    }
  }

  if (error) {
    return (
      <div style={s.wide}>
        <p style={s.errorText}>{error}</p>
        <Link to="/edufads">← Back to the library</Link>
      </div>
    );
  }
  if (!item) return <div style={s.wide}><p style={s.mutedText}>Loading…</p></div>;

  return (
    <article style={{ ...s.wide, maxWidth: 860 }}>
      <p style={{ margin: 0 }}>
        <Link to="/edufads">← Edufads library</Link>
      </p>
      <p style={{ ...s.mutedText, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, margin: `${spacing(6)} 0 ${spacing(2)}` }}>
        {topicLabel(item.topic)}
      </p>
      <h1 style={{ fontFamily: fonts.serif, fontSize: 34, lineHeight: 1.2, margin: `0 0 ${spacing(4)}`, color: colors.ink }}>{item.label}</h1>
      <EvidenceChip strength={item.evidence_strength} />

      <section style={{ ...s.card, marginTop: spacing(6), borderLeft: `6px solid ${colors.gold}` }}>
        <h2 style={{ ...s.heading2, color: colors.brand }}>What research says</h2>
        <p style={s.prose}>{item.summary}</p>
      </section>
      {item.qualifications && (
        <section style={s.card}>
          <h2 style={{ ...s.heading2, color: colors.brand }}>Worth knowing</h2>
          <p style={s.prose}>{item.qualifications}</p>
        </section>
      )}
      {item.alternative_framing && (
        <section style={s.card}>
          <h2 style={{ ...s.heading2, color: colors.brand }}>A research-informed approach</h2>
          <p style={s.prose}>{item.alternative_framing}</p>
        </section>
      )}

      <section style={s.card}>
        <h2 style={{ ...s.heading2, color: colors.brand }}>Sources</h2>
        <ol style={{ paddingLeft: spacing(5), margin: 0 }}>
          {item.sources.map((src) => (
            <li key={src.citation} style={{ marginBottom: spacing(3), lineHeight: 1.55 }}>
              {src.citation}{" "}
              {src.url ? (
                <a href={src.url} target="_blank" rel="noopener noreferrer">
                  {src.identifier}
                </a>
              ) : (
                src.identifier && <span style={s.mutedText}>{src.identifier}</span>
              )}
              <div style={{ display: "flex", gap: spacing(2), alignItems: "center", marginTop: 4, flexWrap: "wrap" }}>
                {src.role && <span style={s.mutedText}>{src.role}</span>}
                {src.verified && <Chip tone="green">Citation checked by a reviewer</Chip>}
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section style={s.card} aria-labelledby="more-research">
        <h2 id="more-research" style={{ ...s.heading2, color: colors.brand }}>More open-access research</h2>
        <HowTheseWereChosen />
        {item.further_reading && item.further_reading.length > 0 ? (
          <ul style={{ margin: 0, padding: 0 }}>
            {item.further_reading.map((r) => (
              <ReadingCard key={r.id ?? r.title} item={r} />
            ))}
          </ul>
        ) : (
          <p style={s.mutedText}>
            {item.further_reading_updated_at
              ? "No article met all the criteria on the last check. The list is refreshed every week."
              : "This list is being prepared — check back soon."}
          </p>
        )}
        <p style={{ margin: `${spacing(3)} 0 0` }}>
          <Link to={`/research?q=${encodeURIComponent(item.search_phrase || item.label)}`}>Search for more research on this idea →</Link>
        </p>
      </section>

      {item.aliases.length > 0 && (
        <p style={s.mutedText}>Also seen as: {item.aliases.map((a) => `“${a}”`).join(", ")}</p>
      )}

      <section style={{ ...s.card, background: colors.goldTint }}>
        <strong>Was this helpful?</strong>{" "}
        {sent ? (
          <span>{sent}</span>
        ) : me ? (
          <span style={{ display: "inline-flex", gap: spacing(2), flexWrap: "wrap", marginLeft: spacing(2) }}>
            <button type="button" style={s.buttonSecondary} onClick={() => void feedback("evidence_card_helpful")}>Yes</button>
            <button type="button" style={s.buttonSecondary} onClick={() => void feedback("evidence_card_unhelpful")}>Not really</button>
          </span>
        ) : (
          <span>
            <Link to="/login">Sign in</Link> to tell curators — your feedback helps improve every note.
          </span>
        )}
        <p style={{ margin: `${spacing(3)} 0 0` }}>
          See something that should change? <Link to="/suggest">Send a suggestion</Link>.
        </p>
      </section>

      <p style={s.mutedText}>
        Evidence base {item.release_version}, published {new Date(item.published_at).toLocaleDateString()}. EduFadder notes are
        for awareness: they summarise published research, not a judgement of any person or page.
      </p>
    </article>
  );
}
